function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Container, FlexboxContainer, CssContainer } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill, Empty, Search, Filters, Delta, Pending, Segmented } from "../../components/analytics-kit";

// territory-common deltas ({text, dir, pct}) in the kit's shape; null stays null ("no comparison").
const kitDelta = d => d && d.pct != null ? {
  dir: d.dir,
  pct: d.pct
} : null;
import ComparisonPicker from "../../components/comparison-picker";
import PlunkDropDown from "../../components/plunk-dropdown";
import PopupAnchor from "../../components/popup-anchor";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import GeoChoropleth from "../../components/geo-choropleth";
import { hasTenantFeature } from "../../functions/tenant-features";
import RangePicker from "../../components/range-picker";
import { rangeLabelFor, RANGES, num, usd, aov, fetchSales, resolveBusinessModel, stateName, deltaOf, deltaOfPaced, comparisonQs, channelLabel, accountLensKv, readAcctLens, writeAcctLens, readBasis, writeBasis, titleCase, isCustomRange, cleanTitle } from "./territory-common";
import { money, placeName, placeList, isHouseRep, isRosterRep, repName, vsLabel, BasisSelect, ShowAll, ChipRow, DeltaText } from "./territory-redesign-kit";
import account from "../../data/account";
import { AccountFilterControl, AccountFilterBanner } from "./account-filter";
import { ActivityComparisonMenu } from "../../components/container-activity-menus";
import compLabel from "../../functions/comp-label";
import { Link } from "react-router-dom";
import { getGlobalRange, getGlobalComp, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import SalesMapCard from "./map-card";

/*
 * Territory & Rep Intelligence — overview (spec: PlunkDocs
 * specs/TERRITORY_REP_INTELLIGENCE_UI.md).
 *
 * §0 foundation: business_model decides which PROJECTION renders
 * (manufacturing → dealer/territory; retail → store/shopper; both →
 * everything + a channel toggle). It never filters data — channel
 * filtering is ?customerType= at runtime.
 *
 * Golden rule: KPI cards and rollups are PURE READS from the
 * pre-aggregated endpoints (COUNT(DISTINCT …) server-side) — never sum
 * roster or per-product rows client-side.
 *
 * G1: the state choropleth click-through routes to Territory Detail
 * (/Territory/:range/region/:code), as do territory table rows.
 *
 * Gated by tenant feature `territoryRep` (registry-driven, default OFF).
 */

class TerritoryViewComponent extends React.Component {
  constructor(props) {
    super(props);
    // Commit the lens: persist to URL (deep-link) + localStorage, then reload the
    // server-filtered sections. Isolate wins over exclude (guarded here + server).
    _defineProperty(this, "setAcctLens", (mode, ids) => {
      ids = [...new Set((ids || []).map(String))];
      const tenantId = (account.get() || {}).tenantId;
      writeAcctLens(tenantId, mode, ids);
      try {
        const qs = new URLSearchParams(window.location.search);
        qs.delete("only");
        qs.delete("exclude");
        if (ids.length) qs.set(mode === "isolate" ? "only" : "exclude", ids.join(","));
        const search = qs.toString();
        this.props.history.replace({
          pathname: window.location.pathname,
          search: search ? "?" + search : ""
        });
      } catch (e) {/* ignore */}
      this.setState({
        acctMode: mode,
        acctIds: ids,
        data: null,
        error: false
      }, () => this.load(this.state.bm, this.state.channel));
    });
    _defineProperty(this, "removeAcct", id => this.setAcctLens(this.state.acctMode, this.state.acctIds.filter(x => String(x) !== String(id))));
    _defineProperty(this, "clearAcct", () => this.setAcctLens(this.state.acctMode, []));
    _defineProperty(this, "onStatus", values => {
      this.setState({
        statusSel: values || [],
        data: null,
        error: false
      }, () => this.load(this.state.bm, this.state.channel));
    });
    _defineProperty(this, "onBasis", v => {
      const basis = v === "ship" ? "ship" : "bill";
      if (basis === this.state.basis) return;
      writeBasis((account.get() || {}).tenantId, basis);
      this.setState({
        basis,
        data: null,
        error: false
      }, () => this.load(this.state.bm, this.state.channel));
    });
    _defineProperty(this, "onRange", e => {
      this.props.history.push("/Territory/" + e.target.value);
    });
    _defineProperty(this, "onChannel", e => {
      const channel = e.target.value;
      this.setState({
        channel,
        data: null,
        error: false
      });
      this.load(this.state.bm, channel);
    });
    _defineProperty(this, "onCompSelected", e => {
      const comp = e.detail.item.dataset.value;
      if (comp === "unavailable" || comp === this.state.comp) return;
      this.setState({
        comp,
        data: null,
        error: false
      });
      this.load(this.state.bm, this.state.channel, comp);
    });
    _defineProperty(this, "openRegion", code => {
      if (!code) return;
      this.props.history.push(`/Territory/${this.range()}/region/${encodeURIComponent(code)}`);
    });
    _defineProperty(this, "openRep", repCode => {
      if (!repCode) return;
      this.props.history.push(`/Territory/${this.range()}/rep/${encodeURIComponent(repCode)}`);
    });
    _defineProperty(this, "openCustomer", accountNumber => {
      if (!accountNumber) return;
      this.props.history.push(`/Territory/${this.range()}/customer/${encodeURIComponent(accountNumber)}`);
    });
    _defineProperty(this, "openChannel", channel => {
      if (!channel) return;
      this.props.history.push(`/Territory/${this.range()}/channel/${encodeURIComponent(channel)}`);
    });
    this.state = {
      data: null,
      error: false,
      showAllReps: false,
      bm: null,
      // business model: manufacturing | retail | both
      channel: "",
      // '' = all; wholesale | retail (bm === 'both' only)
      // Order-status scope (?status=). Empty selection = all booked (the backend
      // default); pick one or more of the tenant's live statuses to narrow. The
      // option list is dynamic — built from /sales/status per tenant.
      statusSel: [],
      // selected statuses (multi); [] = all
      statusList: [],
      // available statuses (dynamic per tenant)
      geoMetric: "revenue",
      geoView: "states",
      // states (G1 choropleth) | map (M1 dealer pins + M4 heat, SALES_TERRITORY_MAPS.md §6.2)
      comp: getGlobalComp("prev_period"),
      // Trend Comparison (global control)
      // Account lens (SALES_ACCOUNT_FILTER.md): exclude/isolate selected accounts.
      acctMode: "exclude",
      acctIds: [],
      // Dealer placement (redesign 2026-10-09): bill = head office (default), ship = delivery location.
      basis: readBasis((account.get() || {}).tenantId),
      repFilter: "selling",
      repQ: "",
      repAll: false,
      mktFilter: "all",
      mktAll: false,
      dealersAll: false,
      xsView: "dealers",
      xsAll: false
    };
    this.marketsRef = /*#__PURE__*/React.createRef();
    this.crossRef = /*#__PURE__*/React.createRef();
    this._alive = true;
  }
  range() {
    return this.props.params && this.props.params.range || getGlobalRange("this_quarter");
  }
  componentDidMount() {
    this.boot();
    this._periodTok = subscribePeriod(({
      range,
      comp
    }) => {
      if (!this._alive) return;
      if (comp && comp !== this.state.comp) this.setState({
        comp
      });
      const target = `/Territory/${range}`;
      if (window.location.pathname === target) {
        this.setState({
          data: null,
          error: false
        }, () => this.load(this.state.bm, this.state.channel, comp || this.state.comp));
      } else {
        this.props.history.push(target);
      }
    });
  }
  componentWillUnmount() {
    this._alive = false;
    unsubscribePeriod(this._periodTok);
  }
  componentDidUpdate(prev) {
    const prevRange = prev.params && prev.params.range || "";
    const curRange = this.props.params && this.props.params.range || "";
    if (prevRange !== curRange) {
      this.setState({
        data: null,
        error: false
      });
      this.load(this.state.bm, this.state.channel);
    }
  }
  async boot() {
    const bm = await resolveBusinessModel();
    if (!this._alive) return;
    const lens = this.readLensFromUrlOrStore();
    this.setState({
      bm,
      acctMode: lens.mode,
      acctIds: lens.ids
    });
    this.load(bm, this.state.channel);
  }
  readLensFromUrlOrStore() {
    const tenantId = (account.get() || {}).tenantId;
    try {
      const qs = new URLSearchParams(this.props.location && this.props.location.search || window.location.search);
      const only = qs.get("only"),
        excl = qs.get("exclude");
      if (only) return {
        mode: "isolate",
        ids: only.split(",").filter(Boolean)
      };
      if (excl) return {
        mode: "exclude",
        ids: excl.split(",").filter(Boolean)
      };
    } catch (e) {/* ignore */}
    return readAcctLens(tenantId);
  }
  async load(bm, channel, comp) {
    const range = this.range();
    comp = comp || this.state.comp;
    const ch = bm === "both" && channel ? channel : "";
    const q = ch ? `?customerType=${ch}` : "";
    const qTop = ch ? `&customerType=${ch}` : "";
    // Append the order-status scope (CSV of selected statuses), choosing ? or &
    // to match what's already on the url. Empty = all booked (backend default).
    const st = (this.state.statusSel || []).join(",");
    const appendKv = (u, kv) => kv ? u + (u.includes("?") ? "&" : "?") + kv : u;
    // Every territory call carries the dealer-placement basis (bill | ship); the backend
    // falls back to ship-to where a dealer has no bill-to, and ignores it where it doesn't apply.
    const wb = u => appendKv(u, "basis=" + (this.state.basis === "ship" ? "ship" : "bill"));
    const ws = u => appendKv(wb(u), st ? "status=" + encodeURIComponent(st) : "");
    // wsa = ws + the account lens (exclude/isolate). Used by every DISPLAY fetch;
    // the customers roster stays on ws (unfiltered) so it can power the picker +
    // banner magnitude, and the Top-dealers table filters client-side.
    const acctKv = accountLensKv(this.state.acctMode, this.state.acctIds);
    const wsa = u => appendKv(ws(u), acctKv);
    const wantMfg = bm !== "retail";
    const wantRetail = bm === "retail" || bm === "both";
    // One combined call for the manufacturer view (/sales/territory-overview): the backend builds every
    // block from one shared scan, so a first load costs one pass instead of ~10 parallel ones that each
    // land on a cold Lambda. Retail blocks still come separately; any failure falls back to the
    // individual calls below.
    if (wantMfg) {
      try {
        const compKv = comparisonQs(range, true, comp).replace(/^\?/, "");
        let u = `/sales/territory-overview/${range}?customersTop=200&productsTop=10`;
        if (ch) u = appendKv(u, `customerType=${ch}`);
        u = appendKv(wsa(u), compKv);
        const [ov, retailCustomers, stores] = await Promise.all([fetchSales(u), wantRetail ? fetchSales(wsa(`/sales/retail-customers/${range}?top=100`)) : Promise.resolve([]), wantRetail ? fetchSales(wsa(`/sales/stores/${range}`)) : Promise.resolve([])]);
        if (ov && ov.totals) {
          const statusList = this.buildStatusList(ov.status || []);
          if (this._alive) this.setState({
            data: {
              reps: ov.reps || [],
              attribution: ov.attribution || {},
              totals: ov.totals,
              customers: ov.customers || [],
              territories: ov.territories || [],
              retailCustomers,
              stores,
              topProducts: ov.products || [],
              channels: ov.channels || []
            },
            statusList
          });
          return;
        }
      } catch (e) {/* fall through to the individual calls */}
    }
    try {
      const cq = comparisonQs(range, !q, comp);
      const [reps, attribution, totals, customers, territories, retailCustomers, stores, topProducts, channels, statusRows] = await Promise.all([fetchSales(wsa(`/sales/reps/${range}${q}${cq}`)), fetchSales(wsa(`/sales/attribution/${range}`)), fetchSales(wsa(`/sales/totals/${range}${q}${cq}`)), wantMfg ? fetchSales(ws(`/sales/customers/${range}?top=200${qTop}`)) : Promise.resolve([]), wantMfg ? fetchSales(wsa(`/sales/territories/${range}${comparisonQs(range, true, comp)}`)) : Promise.resolve([]), wantRetail ? fetchSales(wsa(`/sales/retail-customers/${range}?top=100`)) : Promise.resolve([]), wantRetail ? fetchSales(wsa(`/sales/stores/${range}`)) : Promise.resolve([]), fetchSales(wsa(`/sales/products/${range}?top=10`)).catch(() => []), wantMfg ? fetchSales(wsa(`/sales/channels/${range}${q}${cq}`)).catch(() => []) : Promise.resolve([]), fetchSales(wb(`/sales/status/${range}`)).catch(() => [])]);
      const statusList = this.buildStatusList(statusRows);
      if (this._alive) this.setState({
        data: {
          reps,
          attribution,
          totals,
          customers,
          territories,
          retailCustomers,
          stores,
          topProducts,
          channels
        },
        statusList
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }

  // The tenant's live status vocabulary (/sales/status) → MultiSelect options.
  // Whatever statuses a tenant's ERP emits show up automatically (dynamic).
  buildStatusList(rows) {
    const list = [];
    const seen = new Set();
    (rows || []).forEach(r => {
      const s = r && (r.orderStatus || r.status || r.OrderStatus);
      if (s && s !== "(unspecified)" && !seen.has(s)) {
        seen.add(s);
        list.push(s);
      }
    });
    return list;
  }
  scrollTo(ref) {
    try {
      if (ref && ref.current) ref.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    } catch (e) {/* older browsers */}
  }
  // Choropleth rows for the active projection/channel. Retail stores roll
  // client-side to region ONLY for map shading (their rollup rows are
  // already store-grained server aggregates; region here is presentation
  // grouping, not a KPI source).
  geoRows(data) {
    const {
      bm,
      channel
    } = this.state;
    const useStores = bm === "retail" || bm === "both" && channel === "retail";
    if (!useStores) {
      return (data.territories || []).map(t => ({
        code: t.region,
        revenue: Number(t.totalAmount) || 0,
        orders: Number(t.orders) || 0
      }));
    }
    const byRegion = {};
    (data.stores || []).forEach(s => {
      const code = String(s.region || "").toUpperCase();
      if (!code) return;
      byRegion[code] = byRegion[code] || {
        code,
        revenue: 0,
        orders: 0
      };
      byRegion[code].revenue += Number(s.totalAmount) || 0;
      byRegion[code].orders += Number(s.orders) || 0;
    });
    return Object.values(byRegion);
  }
  // Sales by CHANNEL (rep channel — who sells; distinct from lifecycle
  // b2b/b2c). Hidden entirely until at least one rep carries a channel tag
  // — an all-Unsegmented table is noise for tenants not using channels.
  renderChannels(rows, totals) {
    const tagged = (rows || []).filter(c => String(c.channel || "").toLowerCase() !== "unsegmented");
    if (!tagged.length) return null;
    const totalRev = Number(totals.totalAmount) || 0;
    const sorted = (rows || []).slice().sort((a, b) => (Number(b.totalAmount) || 0) - (Number(a.totalAmount) || 0));
    return /*#__PURE__*/React.createElement(Card, {
      title: "Sales by channel",
      hint: "the selling rep's channel tag \xB7 reps without a tag roll up as Unsegmented \xB7 click a channel for its detail",
      span: 4
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Channel"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "% of Total"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Avg Order Value"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Dealers Buying"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Reps"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "\u0394"))), /*#__PURE__*/React.createElement("tbody", null, sorted.map(c => /*#__PURE__*/React.createElement("tr", {
      key: c.channel,
      className: "clickable cust-row",
      onClick: () => this.openChannel(c.channel),
      title: `Open ${channelLabel(c.channel)} channel detail`
    }, /*#__PURE__*/React.createElement("td", {
      className: "strong"
    }, channelLabel(c.channel)), /*#__PURE__*/React.createElement("td", {
      className: "n strong"
    }, money(c.totalAmount)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, totalRev > 0 ? `${((Number(c.totalAmount) || 0) / totalRev * 100).toFixed(1)}%` : "-"), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(c.orders)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, aov(c.totalAmount, c.orders)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(c.customers)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(c.reps)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, kitDelta(deltaOfPaced(c.totalAmount, c.prevTotalAmount, this.range())) ? /*#__PURE__*/React.createElement(Delta, {
      d: kitDelta(deltaOfPaced(c.totalAmount, c.prevTotalAmount, this.range()))
    }) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "-"))))))));
  }
  hasChannels(rows) {
    return (rows || []).some(c => String(c.channel || "").toLowerCase() !== "unsegmented");
  }

  // Half width when the retail "Top customers" card sits beside it; full width when it is alone.
  renderTopProducts(rows, alone) {
    if (!rows || !rows.length) return null;
    return /*#__PURE__*/React.createElement(Card, {
      title: "Top products",
      hint: "by revenue \xB7 click a product for its detail",
      span: alone ? 12 : 6
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Product"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Units"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"))), /*#__PURE__*/React.createElement("tbody", null, rows.map(p => /*#__PURE__*/React.createElement("tr", {
      key: p.productId,
      className: "clickable cust-row",
      onClick: () => this.props.history.push({
        pathname: `/Territory/${this.range()}/product/${encodeURIComponent(p.productId)}`,
        state: {
          title: p.title
        }
      }),
      title: `Open ${p.title || p.productId} product detail`
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, titleCase(cleanTitle(p.title)) || p.productId), " ", /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, p.sku || p.productId)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(p.orders)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(p.quantity)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, money(p.totalAmount))))))));
  }
  filteredCustomers() {
    const customers = this.state.data && this.state.data.customers || [];
    if (!this.state.acctIds.length) return customers;
    const set = new Set(this.state.acctIds.map(String));
    return this.state.acctMode === "isolate" ? customers.filter(c => set.has(String(c.accountNumber))) : customers.filter(c => !set.has(String(c.accountNumber)));
  }
  renderAccountFilter() {
    return /*#__PURE__*/React.createElement(AccountFilterControl, {
      customers: this.state.data && this.state.data.customers || [],
      mode: this.state.acctMode,
      ids: this.state.acctIds,
      onApply: this.setAcctLens
    });
  }
  renderAcctBanner() {
    return /*#__PURE__*/React.createElement(AccountFilterBanner, {
      customers: this.state.data && this.state.data.customers || [],
      mode: this.state.acctMode,
      ids: this.state.acctIds,
      onRemove: this.removeAcct,
      onClear: this.clearAcct
    });
  }

  // Short phrase for the window inside a sentence ("this year", "last quarter", "in the selected dates").
  periodPhrase() {
    const r = this.range();
    if (isCustomRange(r)) return "in the selected dates";
    if (r === "all_time") return "all time";
    return rangeLabelFor(r, RANGES).toLowerCase();
  }
  comparing() {
    return this.range() !== "all_time";
  }

  // "Worth a look": three plain-language prompts built only from data the page already has.
  // A card is left out when its data isn't there (no invented numbers).
  renderWorthALook({
    isMfg,
    territories,
    houseReps,
    attribution,
    hasAttribution,
    totals
  }) {
    const cards = [];
    if (isMfg) {
      const noRep = (territories || []).filter(t => Number(t.coveringReps) === 0 && Number(t.orders) > 0).sort((a, b) => (Number(b.totalAmount) || 0) - (Number(a.totalAmount) || 0));
      if (noRep.length) {
        const names = noRep.map(t => placeName(t.region));
        const list = names.length <= 2 ? names.join(" and ") : `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
        const rev = noRep.reduce((a, t) => a + (Number(t.totalAmount) || 0), 0);
        cards.push({
          k: "norep",
          t: noRep.length === 1 ? "1 place has sales but no rep" : `${num(noRep.length)} places have sales but no rep`,
          s: `${list}, ${money(rev)} ${this.periodPhrase()}`,
          go: () => this.setState({
            mktFilter: "norep",
            mktAll: true
          }, () => this.scrollTo(this.marketsRef))
        });
      }
    }
    const houseRev = houseReps.reduce((a, r) => a + (Number(r.totalAmount) || 0), 0);
    if (houseRev > 0) {
      const dealers = houseReps.reduce((a, r) => a + (Number(r.customers) || 0), 0);
      const one = houseReps.length === 1 ? houseReps[0] : null;
      cards.push({
        k: "house",
        t: `House accounts: ${money(houseRev)}`,
        s: `${num(dealers)} ${dealers === 1 ? "dealer" : "dealers"} ordered through a house account, with no rep of their own on the order`,
        go: one ? () => this.openRep(one.repCode) : null
      });
    }
    const mism = Number(attribution && attribution.mismatchedOrders) || 0;
    if (hasAttribution && mism > 0) {
      const pct = Number(attribution.mismatchPct);
      cards.push({
        k: "cross",
        t: `${num(mism)} ${mism === 1 ? "order" : "orders"} went to another rep`,
        s: `Sold by a rep who is not the dealer's own${isFinite(pct) && pct > 0 ? ` (${pct}% of orders)` : ""}`,
        go: () => this.scrollTo(this.crossRef)
      });
    }
    if (!cards.length) return null;
    return /*#__PURE__*/React.createElement("section", {
      className: "tr-worth"
    }, /*#__PURE__*/React.createElement("h2", {
      className: "tr-worth-h"
    }, "Worth a look"), /*#__PURE__*/React.createElement("div", {
      className: "tr-worth-grid"
    }, cards.map(c => /*#__PURE__*/React.createElement("a", {
      key: c.k,
      href: "#",
      className: `tr-worth-card${c.go ? "" : " static"}`,
      onClick: e => {
        e.preventDefault();
        if (c.go) c.go();
      }
    }, /*#__PURE__*/React.createElement("b", null, c.t), /*#__PURE__*/React.createElement("span", null, c.s)))));
  }
  renderReps({
    realReps,
    hasAttribution
  }) {
    const {
      repFilter,
      repQ,
      repAll,
      comp
    } = this.state;
    const selling = realReps.filter(r => Number(r.orders) > 0);
    const idle = realReps.filter(r => !(Number(r.orders) > 0));
    const q = repQ.trim().toLowerCase();
    let rows = repFilter === "nosales" ? idle : repFilter === "all" ? realReps : selling;
    if (q) {
      rows = rows.filter(r => [r.name, r.repCode, placeList(r.states, 99), r.channel].some(v => String(v || "").toLowerCase().includes(q)));
    }
    const maxRev = Math.max(1, ...realReps.map(r => Number(r.totalAmount) || 0));
    const totalRev = realReps.reduce((a, r) => a + (Number(r.totalAmount) || 0), 0) || 1;
    const LIMIT = 10;
    const shown = repAll ? rows : rows.slice(0, LIMIT);
    const comparing = this.comparing();
    const note = (this.state.statusSel || []).length ? "Counts orders with the selected statuses." : "Counts every booked order.";
    return /*#__PURE__*/React.createElement(Card, {
      title: "Reps",
      hint: `${num(realReps.length)} active reps · click a rep for their page`,
      cls: "tr-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "tr-toolbar"
    }, /*#__PURE__*/React.createElement(ChipRow, {
      value: repFilter,
      onChange: v => this.setState({
        repFilter: v,
        repAll: false
      }),
      options: [{
        v: "selling",
        l: "Selling",
        n: selling.length
      }, {
        v: "nosales",
        l: "No sales",
        n: idle.length
      }, {
        v: "all",
        l: "All",
        n: realReps.length
      }]
    }), /*#__PURE__*/React.createElement(Search, {
      value: repQ,
      onChange: v => this.setState({
        repQ: v
      }),
      placeholder: "Search reps"
    })), /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table tr-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Rep"), /*#__PURE__*/React.createElement("th", null, "Covers"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Dealers this rep sold to in the period"
    }, "Dealers"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      className: "tr-share-h",
      title: "Bar length is against the top rep; hover a bar for the share of all rep revenue"
    }, "Share of revenue"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"), comparing ? /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, vsLabel(comp)) : null)), /*#__PURE__*/React.createElement("tbody", null, shown.map(r => {
      const rev = Number(r.totalAmount) || 0;
      return /*#__PURE__*/React.createElement("tr", {
        key: r.repCode,
        className: "clickable" + (r.status === "terminated" ? " terminated" : ""),
        onClick: () => this.openRep(r.repCode),
        title: `Open ${repName(r)}`
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, repName(r)), /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, "Rep ", r.repCode, r.channel ? ` · ${channelLabel(r.channel)}` : "", r.status === "terminated" ? " · no longer active" : "")), /*#__PURE__*/React.createElement("td", {
        className: "tr-covers",
        title: placeList(r.states, 99)
      }, r.states ? placeList(r.states) : /*#__PURE__*/React.createElement("span", {
        className: "dim"
      }, Number(r.postalCodes) > 0 ? "" : "No states assigned"), Number(r.postalCodes) > 0 ? /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, num(r.postalCodes), " ZIP codes") : null), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(r.customers)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(r.orders)), /*#__PURE__*/React.createElement("td", {
        title: `${(rev / totalRev * 100).toFixed(1)}% of all rep revenue`
      }, /*#__PURE__*/React.createElement("span", {
        className: "tr-bar"
      }, /*#__PURE__*/React.createElement("span", {
        style: {
          width: `${Math.max(rev > 0 ? 2 : 0, rev / maxRev * 100)}%`
        }
      }))), /*#__PURE__*/React.createElement("td", {
        className: "n strong"
      }, money(rev)), comparing ? /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, /*#__PURE__*/React.createElement(DeltaText, {
        d: kitDelta(deltaOfPaced(r.totalAmount, r.prevTotalAmount, this.range())),
        isNew: rev > 0 && !(Number(r.prevTotalAmount) > 0)
      })) : null);
    }), !shown.length ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      colSpan: comparing ? 7 : 6
    }, /*#__PURE__*/React.createElement(Empty, null, q ? "No reps match that search." : repFilter === "nosales" ? "Every rep has sales in this period." : "No rep sales in this period."))) : null))), /*#__PURE__*/React.createElement(ShowAll, {
      total: rows.length,
      shown: LIMIT,
      open: repAll,
      onToggle: () => this.setState({
        repAll: !repAll
      }),
      note: hasAttribution ? `${note} A rep's revenue is the orders they sold, whoever's dealer it was.` : `${note} Orders don't carry a rep yet, so a rep's revenue is their own dealers' orders.`
    }));
  }
  renderMarkets(territories) {
    const {
      mktFilter,
      mktAll,
      comp
    } = this.state;
    const all = (territories || []).slice().sort((a, b) => (Number(b.totalAmount) || 0) - (Number(a.totalAmount) || 0));
    const noRep = all.filter(t => Number(t.coveringReps) === 0);
    const rows = mktFilter === "norep" ? noRep : all;
    const LIMIT = 10;
    const shown = mktAll ? rows : rows.slice(0, LIMIT);
    const comparing = this.comparing();
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-card c6 tr-anchor",
      ref: this.marketsRef
    }, /*#__PURE__*/React.createElement("h2", null, "Markets", /*#__PURE__*/React.createElement("span", {
      className: "hint",
      title: "No rep: no active rep is assigned to the state (house accounts don't count). The Sales cockpit's Coverage tab also lists states with dealers but no orders in the period."
    }, num(all.length), " states and provinces with orders \xB7 click one for that market")), /*#__PURE__*/React.createElement("div", {
      className: "tr-toolbar"
    }, /*#__PURE__*/React.createElement(ChipRow, {
      value: mktFilter,
      onChange: v => this.setState({
        mktFilter: v,
        mktAll: false
      }),
      options: [{
        v: "all",
        l: "All",
        n: all.length
      }, {
        v: "norep",
        l: "No rep",
        n: noRep.length
      }]
    })), /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table tr-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Market"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Reps assigned to cover this state or province"
    }, "Reps"), /*#__PURE__*/React.createElement("th", {
      title: "Dealers who ordered in this period, out of all dealers in the market"
    }, "Dealers buying"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"), comparing ? /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, comp === "prev_year" ? "vs LY" : "vs prev") : null)), /*#__PURE__*/React.createElement("tbody", null, shown.map(t => /*#__PURE__*/React.createElement("tr", {
      key: t.region,
      className: "clickable",
      onClick: () => this.openRegion(t.region),
      title: `Open ${placeName(t.region)}`
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "tr-name-row"
    }, /*#__PURE__*/React.createElement("span", {
      className: "strong"
    }, placeName(t.region)), Number(t.coveringReps) === 0 ? /*#__PURE__*/React.createElement("span", {
      className: "tr-flag warn"
    }, "No rep") : null)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(t.coveringReps)), /*#__PURE__*/React.createElement("td", null, num(t.activeDealers), " of ", num(t.totalDealers)), /*#__PURE__*/React.createElement("td", {
      className: "n strong"
    }, money(t.totalAmount)), comparing ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement(DeltaText, {
      d: kitDelta(deltaOfPaced(t.totalAmount, t.prevTotalAmount, this.range())),
      isNew: Number(t.totalAmount) > 0 && !(Number(t.prevTotalAmount) > 0)
    })) : null)), !shown.length ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      colSpan: comparing ? 5 : 4
    }, /*#__PURE__*/React.createElement(Empty, null, mktFilter === "norep" ? "Every market with sales has a rep." : "No market sales in this period."))) : null))), /*#__PURE__*/React.createElement(ShowAll, {
      total: rows.length,
      shown: LIMIT,
      open: mktAll,
      onToggle: () => this.setState({
        mktAll: !mktAll
      }),
      note: "“Dealers buying” is dealers who ordered in this period out of all dealers in the market."
    }));
  }
  renderTopDealers() {
    const {
      dealersAll,
      basis
    } = this.state;
    const rows = this.filteredCustomers().slice(0, 25);
    const shown = dealersAll ? rows : rows.slice(0, 10);
    return /*#__PURE__*/React.createElement(Card, {
      title: "Top dealers",
      hint: `by revenue · city is ${basis === "ship" ? "the delivery location" : "the head office"}`,
      span: 6,
      right: /*#__PURE__*/React.createElement("a", {
        className: "tr-link",
        href: "#",
        onClick: e => {
          e.preventDefault();
          this.props.history.push(`/Dealers/${this.range()}`);
        }
      }, "Dealer performance")
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table tr-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Dealer"), /*#__PURE__*/React.createElement("th", null, "Rep"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"))), /*#__PURE__*/React.createElement("tbody", null, shown.map(c => {
      const rep = c.assignedRepName || c.assignedRepId;
      const house = isHouseRep({
        name: c.assignedRepName,
        repCode: c.assignedRepId
      });
      return /*#__PURE__*/React.createElement("tr", {
        key: c.accountNumber,
        className: "clickable",
        onClick: () => this.openCustomer(c.accountNumber),
        title: `Open ${titleCase(c.name || c.accountNumber)}`
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, titleCase(c.name || c.accountNumber)), /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, [c.city ? titleCase(c.city) : null, c.region].filter(Boolean).join(", ") || `Account ${c.accountNumber}`)), /*#__PURE__*/React.createElement("td", null, house ? "House account" : rep ? titleCase(rep) : /*#__PURE__*/React.createElement("span", {
        className: "dim"
      }, "No rep")), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(c.orders)), /*#__PURE__*/React.createElement("td", {
        className: "n strong"
      }, money(c.totalAmount)));
    }), !shown.length ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      colSpan: 4
    }, /*#__PURE__*/React.createElement(Empty, null, "No dealer sales in this period."))) : null))), /*#__PURE__*/React.createElement(ShowAll, {
      total: rows.length,
      shown: 10,
      open: dealersAll,
      onToggle: () => this.setState({
        dealersAll: !dealersAll
      }),
      allLabel: `Show top ${num(rows.length)}`,
      fewerLabel: "Show top 10"
    }));
  }

  // Orders sold by a rep who isn't the dealer's own (rep attribution). By dealer (default) or by rep.
  renderCrossSold(attribution) {
    const {
      xsView,
      xsAll
    } = this.state;
    const accounts = attribution.topCrossSoldAccounts || [];
    const sellers = (attribution.byRep || []).filter(r => Number(r.otherAccountOrders) > 0).sort((a, b) => (Number(b.otherAccountOrders) || 0) - (Number(a.otherAccountOrders) || 0));
    if (!accounts.length && !sellers.length) return null;
    const mism = Number(attribution.mismatchedOrders) || 0;
    const pct = Number(attribution.mismatchPct);
    const view = xsView === "reps" || !accounts.length ? "reps" : "dealers";
    const rows = view === "reps" ? sellers : accounts;
    const LIMIT = 5;
    const shown = xsAll ? rows : rows.slice(0, LIMIT);
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-card c12 tr-anchor",
      ref: this.crossRef
    }, /*#__PURE__*/React.createElement("h2", null, "Orders sold by another rep", /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, num(mism), " ", mism === 1 ? "order" : "orders", isFinite(pct) && pct > 0 ? `, ${pct}% of all orders` : "")), /*#__PURE__*/React.createElement("div", {
      className: "tr-toolbar spread"
    }, /*#__PURE__*/React.createElement("p", {
      className: "tr-explain"
    }, "The dealer belongs to one rep, but a different rep sold the order. The dealer's own rep won't see these orders in a view of just their sales."), accounts.length && sellers.length ? /*#__PURE__*/React.createElement(Segmented, {
      options: [["dealers", "By dealer"], ["reps", "By rep"]],
      value: view,
      onChange: v => this.setState({
        xsView: v,
        xsAll: false
      })
    }) : null), /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table tr-table"
    }, view === "dealers" ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Dealer"), /*#__PURE__*/React.createElement("th", null, "Dealer's own rep"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Orders on this dealer sold by a rep other than its own"
    }, "Orders by another rep"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Revenue at this dealer in the period"
    }, "Revenue"))), /*#__PURE__*/React.createElement("tbody", null, shown.map(a => {
      const own = a.assignedRepName || a.assignedRepId;
      const house = isHouseRep({
        name: a.assignedRepName,
        repCode: a.assignedRepId
      });
      return /*#__PURE__*/React.createElement("tr", {
        key: a.accountNumber,
        className: "clickable",
        onClick: () => this.openCustomer(a.accountNumber),
        title: `Open ${titleCase(a.name || a.accountNumber)}`
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, titleCase(a.name || a.accountNumber)), /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, "Account ", a.accountNumber)), /*#__PURE__*/React.createElement("td", null, house ? "House account" : own ? titleCase(own) : /*#__PURE__*/React.createElement("span", {
        className: "dim"
      }, "No rep")), /*#__PURE__*/React.createElement("td", {
        className: "n strong"
      }, num(a.ordersByOthers)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, money(a.totalAmount)));
    }))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Rep"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Orders on dealers assigned to this rep"
    }, "Orders on their own dealers"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Orders on dealers that belong to another rep"
    }, "Orders on other reps' dealers"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"))), /*#__PURE__*/React.createElement("tbody", null, shown.map(r => /*#__PURE__*/React.createElement("tr", {
      key: r.repCode,
      className: "clickable",
      onClick: () => this.openRep(r.repCode),
      title: `Open ${repName(r)}`
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, repName(r)), /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, "Rep ", r.repCode)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(r.ownAccountOrders)), /*#__PURE__*/React.createElement("td", {
      className: "n strong"
    }, num(r.otherAccountOrders)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, money(r.totalAmount)))))))), /*#__PURE__*/React.createElement(ShowAll, {
      total: rows.length,
      shown: LIMIT,
      open: xsAll,
      onToggle: () => this.setState({
        xsAll: !xsAll
      }),
      allLabel: `Show all ${num(rows.length)} ${view === "reps" ? "reps" : "dealers"}`
    }));
  }
  render() {
    if (!hasTenantFeature("territoryRep")) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Territory & Rep Intelligence is not configured for this workspace. It requires the B2B sales feeds (reps, dealer accounts, wholesale transactions). Contact Plunk to enable it."));
    }
    const {
      data,
      error,
      bm,
      channel,
      geoMetric,
      geoView,
      basis
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (!data || !bm) return /*#__PURE__*/React.createElement(Loader, null);
    const {
      reps,
      territories,
      attribution,
      totals,
      retailCustomers,
      stores
    } = data;
    // "both" reads as a retailer unless the Wholesale channel is selected — dealer/rep vocabulary
    // only where the wholesale book is in view (Shopify B2B stores, 2026-09-27).
    const isMfg = bm === "manufacturing" || bm === "both" && channel === "wholesale";
    const isRetail = bm === "retail" || bm === "both";
    // House / no-rep placeholder rows are not reps: they surface in "Worth a look" instead.
    const realReps = (reps || []).filter(r => !isHouseRep(r) && isRosterRep(r));
    const houseReps = (reps || []).filter(r => isHouseRep(r));
    const sellingReps = realReps.filter(r => Number(r.orders) > 0);
    // false = the tenant has no per-order sold-by data (e.g. pre-ERP feed);
    // attribution is empty BY CONSTRUCTION — show "not available yet", never 0%.
    // Older API builds don't send the flag: treat undefined as available.
    const hasAttribution = attribution.hasPerOrderRepData !== false;
    const activeTerritories = (territories || []).filter(t => t.orders > 0).length;
    const activeStores = (stores || []).filter(s => s.orders > 0).length;
    const geoRows = this.geoRows(data);
    const range = this.range();
    const avg = Number(totals.orders) > 0 ? (Number(totals.totalAmount) || 0) / Number(totals.orders) : 0;
    const placeWord = basis === "ship" ? "delivery location" : "head office";
    const idleReps = realReps.length - sellingReps.length;
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view tr-view wa-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      title: "Territory & Reps",
      subtitle: `${isMfg ? "Who sells where, and how each rep and market is doing" : "Retail sales by store and customer"} · ${rangeLabelFor(range, RANGES)}`,
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, bm === "both" && /*#__PURE__*/React.createElement(PlunkDropDown, {
        width: "150px",
        value: channel,
        onChange: v => this.onChannel({
          target: {
            value: v
          }
        }),
        options: [["", "All Channels"], ["wholesale", "Wholesale"], ["retail", "Retail"]]
      }), /*#__PURE__*/React.createElement(PopupAnchor, null, anchor => /*#__PURE__*/React.createElement(MultiSelect, {
        data: this.state.statusList,
        value: this.state.statusSel,
        onChange: e => this.onStatus(e.value),
        placeholder: "All statuses",
        style: {
          width: 240
        },
        popupSettings: anchor ? {
          appendTo: anchor
        } : undefined
      })), isMfg && /*#__PURE__*/React.createElement(BasisSelect, {
        value: basis,
        onChange: this.onBasis
      }), isMfg && this.renderAccountFilter())
    }), this.renderAcctBanner(), /*#__PURE__*/React.createElement(Ribbon, {
      comparing: true,
      tiles: [{
        l: "Revenue",
        v: money(totals.totalAmount),
        d: kitDelta(deltaOfPaced(totals.totalAmount, totals.prevTotalAmount, range)),
        t: "Booked revenue in the period"
      }, {
        l: "Orders",
        v: num(totals.orders),
        d: kitDelta(deltaOfPaced(totals.orders, totals.prevOrders, range)),
        s: avg > 0 ? `${money(avg)} average` : "",
        t: "Orders in the period, with the average order value"
      }, {
        l: isMfg ? "Dealers buying" : "Customers buying",
        v: num(totals.customers),
        d: kitDelta(deltaOfPaced(totals.customers, totals.prevCustomers, range)),
        t: isMfg ? "Dealers who placed at least one order in the period" : "Customers who placed at least one order in the period"
      }, realReps.length > 0 ? {
        l: "Reps selling",
        v: `${num(sellingReps.length)} of ${num(realReps.length)}`,
        s: idleReps > 0 ? `${num(idleReps)} ${idleReps === 1 ? "rep has" : "reps have"} no sales yet` : "every rep has sales",
        t: "Reps with at least one order in the period, out of your active reps (terminated reps and house accounts not counted)",
        noDelta: true
      } : {
        l: "Average order",
        v: avg > 0 ? money(avg) : "-----"
      }, isMfg ? {
        l: "States and provinces",
        v: num(activeTerritories),
        s: "with orders",
        noDelta: true,
        t: `Places with at least one order, dealers placed by ${placeWord}`
      } : {
        l: "Active stores",
        v: num(activeStores)
      }]
    }), this.renderWorthALook({
      isMfg,
      territories,
      houseReps,
      attribution,
      hasAttribution,
      totals
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, geoRows.length > 0 && geoView === "states" && /*#__PURE__*/React.createElement(Card, {
      title: "Sales by state",
      hint: isMfg ? `dealers placed by ${placeWord} · demand heat always by delivery location · click a state for that market` : "click a state for that market",
      span: isMfg && this.hasChannels(data.channels) ? 8 : 12,
      right: /*#__PURE__*/React.createElement("span", {
        className: "sx-chips"
      }, /*#__PURE__*/React.createElement("span", {
        className: "seg"
      }, [["states", "States"], ["map", "Coverage map"]].map(([v, l]) => /*#__PURE__*/React.createElement("button", {
        key: v,
        className: geoView === v ? "on" : "",
        onClick: () => this.setState({
          geoView: v
        })
      }, l))), /*#__PURE__*/React.createElement(PlunkDropDown, {
        width: "150px",
        value: geoMetric,
        onChange: v => this.setState({
          geoMetric: v
        }),
        options: [["revenue", "Revenue"], ["orders", "Orders"], ["aov", "Avg order value"]]
      }))
    }, /*#__PURE__*/React.createElement(GeoChoropleth, {
      rows: geoRows,
      metric: geoMetric,
      onSelect: this.openRegion,
      legendFormat: geoMetric === "orders" ? undefined : "$#,##0"
    })), geoView === "map" && /*#__PURE__*/React.createElement(SalesMapCard, {
      key: `${range}|${basis}`,
      range: range,
      basis: basis,
      want: {
        coverage: isMfg,
        dealers: true,
        heat: true,
        stores: isRetail
      },
      always: true,
      title: isMfg ? "Coverage, dealers and demand on the map" : "Dealers and demand on the map",
      hint: `rep coverage at ZIP grain · pins = dealers by ${placeWord} ZIP, sized by revenue, coloured by lapse band · heat = ZIP3 demand against booked revenue, always by delivery location · pins are ZIP centroids until a row is geocoded by address`,
      span: isMfg && this.hasChannels(data.channels) ? 8 : 12,
      right: /*#__PURE__*/React.createElement("span", {
        className: "seg"
      }, [["states", "States"], ["map", "Coverage map"]].map(([v, l]) => /*#__PURE__*/React.createElement("button", {
        key: v,
        className: geoView === v ? "on" : "",
        onClick: () => this.setState({
          geoView: v
        })
      }, l))),
      emptyText: "Nothing on the map yet. Dealer pins need ZIP codes on the customer feed and the nightly geocode; heat needs website visits with postal geo. The States view always works.",
      onDealer: p => this.openCustomer(p.accountNumber || p.id),
      onStore: p => this.props.history.push(`/Stores/${range}/store/${encodeURIComponent(p.id)}`),
      onHeat: z => z.state && this.openRegion(z.state)
    }), isMfg && this.renderChannels(data.channels, totals), realReps.length > 0 && this.renderReps({
      realReps,
      hasAttribution
    }), isMfg && this.renderMarkets(territories), isMfg && this.renderTopDealers(), isMfg && hasAttribution && this.renderCrossSold(attribution), isRetail && (stores || []).length > 0 && /*#__PURE__*/React.createElement(Card, {
      title: "Store performance",
      span: 6
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Store"), /*#__PURE__*/React.createElement("th", null, "Region"), /*#__PURE__*/React.createElement("th", null, "Group"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Customers"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"))), /*#__PURE__*/React.createElement("tbody", null, stores.map((s, i) => /*#__PURE__*/React.createElement("tr", {
      key: s.locationId || i
    }, /*#__PURE__*/React.createElement("td", {
      className: "strong"
    }, s.store || "-"), /*#__PURE__*/React.createElement("td", null, s.region ? placeName(s.region) : "-"), /*#__PURE__*/React.createElement("td", null, s.locationGroup || "-"), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(s.customers)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(s.orders)), /*#__PURE__*/React.createElement("td", {
      className: "n strong"
    }, money(s.totalAmount)))))))), this.renderTopProducts(data.topProducts, !(isRetail && (retailCustomers || []).length > 0)), isRetail && (retailCustomers || []).length > 0 && /*#__PURE__*/React.createElement(Card, {
      title: "Top customers",
      hint: "top 25 by revenue",
      span: 6
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Customer"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"))), /*#__PURE__*/React.createElement("tbody", null, retailCustomers.slice(0, 25).map((c, i) => /*#__PURE__*/React.createElement("tr", {
      key: c.customerId || i
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, c.customerId === "shopify:anonymous" ? "Guest checkout" : c.name ? titleCase(c.name) : c.customerId)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(c.orders)), /*#__PURE__*/React.createElement("td", {
      className: "n strong"
    }, money(c.totalAmount))))))))));
  }
}
export default withRouter(TerritoryViewComponent);